/**
 * @prettier
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { useTranslation, withTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";

import Header from "../../components/Header";
import CustomButton from "../../components/CustomButton";
import TextInput from "../EverificationPage/components/TextInput";
import SendEmailToast from "./components/SendEmailToast";
import Navigator from "../../components/Navigator";
import idPalLogo from "../../assets/img/idpal_logo.svg";
import { imageAlt } from "../../config/accessabilityRules";
import { groupLinkSend } from "../../config/groupLinkSend";
import { revalidateField } from "../../services/shared/eVerificationValidatorService";
import { isInIframe } from "../../services/shared/helpers";
import ApiService from "../../services/api/api";
import DataDogService from "../../services/shared/datadogService";
import { isGroupLinkShareLimitExceeded } from "../../services/shared/groupLinkShareLimit";

const GROUP_LINK_TOAST_ERROR_KEYS = new Set([
    "idpal_group_link_failed_to_send_to",
    "idpal_group_link_share_limit_reached",
]);

const FIELD_CONFIG = {
    name: groupLinkSend["group-link-send-name"],
    email: groupLinkSend["group-link-send-email"],
};

const GroupLinkSendPage = ({ branding, i18n }) => {
    const { t } = useTranslation();
    const primaryFocusRef = useRef(null);
    const recaptchaRef = useRef(null);
    const formRef = useRef(null);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [errors, setErrors] = useState({});
    const [sentToEmail, setSentToEmail] = useState("");
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const [toastMessageKey, setToastMessageKey] = useState(
        "idpal_group_link_sent_to"
    );
    const [showCustomClientsLogo, setShowCustomClientsLogo] = useState(false);
    const [isRecaptchaEmpty, setIsRecaptchaEmpty] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // TextInput is uncontrolled, so bump this to remount the fields and clear them.
    const [formResetKey, setFormResetKey] = useState(0);

    const customClientsLogo = branding?.logo || null;

    const customLogo = useMemo(() => {
        if (branding?.logo) {
            return `data:image/png;base64,${branding.logo}`;
        }
        return null;
    }, [branding?.logo]);

    const toastMessage = useMemo(() => {
        if (toastMessageKey === "idpal_group_link_share_limit_reached") {
            return t("idpal_group_link_share_limit_reached");
        }
        return t(toastMessageKey, { email: sentToEmail });
    }, [toastMessageKey, sentToEmail, t]);

    useEffect(() => {
        if (primaryFocusRef.current) {
            primaryFocusRef.current.focus();
        }
        if (customClientsLogo && !isInIframe()) {
            setShowCustomClientsLogo(true);
        }
        document.title = t("idpal_doc_title_group_link_send");
    }, [customClientsLogo, t]);

    const focusFirstErrorField = nextErrors => {
        if (!formRef.current) {
            return;
        }

        const firstErrorFieldKey = ["name", "email"].find(
            fieldKey => nextErrors[fieldKey]
        );
        if (!firstErrorFieldKey) {
            return;
        }

        const fieldId = FIELD_CONFIG[firstErrorFieldKey].id;
        const field = formRef.current.querySelector(`input#${fieldId}`);
        if (field) {
            field.focus();
        }
    };

    const validate = () => {
        const nextErrors = {};
        const nameError = revalidateField(
            FIELD_CONFIG.name.id,
            name,
            groupLinkSend
        );
        const emailError = revalidateField(
            FIELD_CONFIG.email.id,
            email,
            groupLinkSend
        );

        if (nameError) {
            nextErrors.name = nameError;
        }
        if (emailError) {
            nextErrors.email = emailError;
        }

        setErrors(nextErrors);

        const hasErrors = Object.keys(nextErrors).length > 0;
        if (hasErrors) {
            focusFirstErrorField(nextErrors);
        }

        return !hasErrors;
    };

    const revalidateFieldAfterUpdate = (fieldKey, fieldId, value) => {
        if (!errors[fieldKey]) {
            return;
        }

        const errorMessage = revalidateField(fieldId, value, groupLinkSend);
        setErrors(prev => {
            const nextErrors = { ...prev };
            if (errorMessage === null) {
                delete nextErrors[fieldKey];
            } else {
                nextErrors[fieldKey] = errorMessage;
            }
            return nextErrors;
        });
    };

    const handleDismissToast = () => {
        setShowSuccessToast(false);
    };

    const dismissSuccessToastIfVisible = () => {
        setShowSuccessToast(isVisible => (isVisible ? false : isVisible));
    };

    const handleNameChange = event => {
        const value = event.target.value;
        dismissSuccessToastIfVisible();
        setName(value);
        revalidateFieldAfterUpdate("name", FIELD_CONFIG.name.id, value);
    };

    const handleEmailChange = event => {
        const value = event.target.value;
        dismissSuccessToastIfVisible();
        setEmail(value);
        revalidateFieldAfterUpdate("email", FIELD_CONFIG.email.id, value);
    };

    const resetForm = () => {
        setName("");
        setEmail("");
        setErrors({});
        setIsRecaptchaEmpty(false);
        setFormResetKey(prev => prev + 1);
        recaptchaRef.current?.reset();
    };

    const setRecaptchaError = () => {
        recaptchaRef.current?.reset();
        setIsRecaptchaEmpty(true);
    };

    const handleRecaptchaChange = () => {
        setIsRecaptchaEmpty(false);
    };

    const handleSubmit = event => {
        if (event && typeof event.preventDefault === "function") {
            event.preventDefault();
        }
        if (isSubmitting) {
            return;
        }
        if (!validate()) {
            return;
        }

        const recaptchaToken = recaptchaRef.current?.getValue();
        if (!recaptchaToken) {
            setRecaptchaError();
            return;
        }

        const trimmedEmail = email.trim();
        const trimmedName = name.trim();
        const emails = [trimmedEmail];

        setIsSubmitting(true);

        ApiService.postGroupLinkSend(trimmedName, emails, recaptchaToken)
            .then(() => {
                setSentToEmail(trimmedEmail);
                setToastMessageKey("idpal_group_link_sent_to");
                resetForm();
                setShowSuccessToast(true);
            })
            .catch(error => {
                if (isGroupLinkShareLimitExceeded(error)) {
                    setSentToEmail("");
                    setToastMessageKey("idpal_group_link_share_limit_reached");
                } else {
                    DataDogService.createError("Unable to send group link.");
                    setSentToEmail(trimmedEmail);
                    setToastMessageKey("idpal_group_link_failed_to_send_to");
                }
                setShowSuccessToast(true);
                recaptchaRef.current?.reset();
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <div className="root-layout-container group-link-send-page">
            <Header showLanguageSelect isLogoHidden={true} />
            <section className="content-wrapper">
                <div className="grouplink-send-logo-container">
                    {showCustomClientsLogo ? (
                        <img
                            alt={imageAlt.startLogo}
                            className="custom-logo-image"
                            src={customLogo}
                        />
                    ) : (
                        <img
                            className="custom-logo-image"
                            src={idPalLogo}
                            alt="ID-Pal Logo"
                        />
                    )}
                </div>
                <SendEmailToast
                    isVisible={showSuccessToast}
                    isError={GROUP_LINK_TOAST_ERROR_KEYS.has(toastMessageKey)}
                    message={toastMessage}
                    dismissLabel={t("idpal_dismiss_notification")}
                    onDismiss={handleDismissToast}
                />
                <div className="grouplink-send-content-container">
                    <div className="grouplink-send-content">
                        <h1
                            ref={primaryFocusRef}
                            tabIndex={-1}
                            className="grouplink-send-text-container"
                        >
                            {t("idpal_group_link_send_title")}
                        </h1>
                        <h2 className="grouplink-send-description-text">
                            {t("idpal_group_link_send_description")}
                        </h2>

                        <form
                            ref={formRef}
                            className="grouplink-send-form"
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <TextInput
                                key={`name-${formResetKey}`}
                                label={t("idpal_name")}
                                placeholder={t("idpal_name")}
                                isRequired
                                config={FIELD_CONFIG.name}
                                error={errors.name}
                                onChange={handleNameChange}
                                defaultValue={name}
                                reserveErrorSpace={true}
                            />
                            <TextInput
                                key={`email-${formResetKey}`}
                                label={t("idpal_email")}
                                placeholder={t("idpal_email")}
                                isRequired
                                config={FIELD_CONFIG.email}
                                error={errors.email}
                                onChange={handleEmailChange}
                                defaultValue={email}
                                inputProps={{ type: "email" }}
                                reserveErrorSpace={true}
                            />
                        </form>

                        <div className="grouplink-send-recaptcha-wrapper">
                            <div className="grouplink-send-recaptcha-content">
                                <div
                                    key={i18n.language}
                                    className={
                                        isRecaptchaEmpty
                                            ? "g-recaptcha-error-border"
                                            : ""
                                    }
                                >
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={process.env.REACT_APP_SITE_KEY}
                                        hl={i18n.language}
                                        onChange={handleRecaptchaChange}
                                    />
                                </div>
                                {isRecaptchaEmpty && (
                                    <p className="g-recaptcha-error">
                                        {t("idpal_recaptcha_prompt")}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="grouplink-send-cta-container">
                        <CustomButton
                            id="group-link-send-email-button"
                            className="btn"
                            label={t("idpal_send_email")}
                            type="button"
                            isDisabled={isSubmitting}
                            handleClick={handleSubmit}
                        />
                    </div>
                </div>
            </section>
            <Navigator
                page={"group_link_send_page"}
                action={"load"}
                propsToPass={null}
            />
        </div>
    );
};

function mapStateToProps(state) {
    return {
        branding: state.config.profile?.data?.company_branding,
    };
}

export default withTranslation("translation")(
    connect(mapStateToProps)(GroupLinkSendPage)
);
